// components/modals/ValidationResultsModal.tsx

import React from "react";
import { createPortal } from "react-dom";
import "../../styles/modals/ValidationResultsModal.css";

interface ValidationResult {
  valid: boolean;
  errors: Array<{
    type: string;
    message: string;
    nodeId?: string;
    nodeLabel?: string;
    field?: string;
  }>;
  warnings: Array<{
    type: string;
    message: string;
    nodeId?: string;
    nodeLabel?: string;
    nodes?: Array<{ id: string; label: string }>;
  }>;
  nodeCount: number;
  edgeCount: number;
  maxDepth?: number;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  result: ValidationResult | null;
}

export const ValidationResultsModal: React.FC<Props> = ({
  visible,
  onClose,
  result,
}) => {
  if (!visible || !result) return null;

  const { valid, errors, warnings, nodeCount, edgeCount, maxDepth } = result;

  // Group errors by node
  const errorsByNode = errors.reduce((acc: any, error) => {
    const key = error.nodeId || "workflow";
    if (!acc[key]) {
      acc[key] = {
        nodeLabel: error.nodeLabel || "Workflow",
        nodeId: error.nodeId,
        errors: [],
      };
    }
    acc[key].errors.push(error);
    return acc;
  }, {});

  return createPortal(
    <div className="validation-modal-overlay" onClick={onClose}>
      <div className="validation-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`validation-header ${valid ? "valid" : "invalid"}`}>
          <div className="validation-status">
            <div className={`status-badge ${valid ? "success" : "error"}`}>
              {valid ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20 6L9 17L4 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <div className="status-text">
              <h2>{valid ? "Validation Passed" : "Validation Failed"}</h2>
              <p>
                {nodeCount} node{nodeCount !== 1 ? "s" : ""} • {edgeCount}{" "}
                connection{edgeCount !== 1 ? "s" : ""}
                {maxDepth ? ` • Depth ${maxDepth}` : ""}
              </p>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Stats */}
        <div className="validation-stats">
          <div className="stat-item">
            <div className="stat-value">{nodeCount}</div>
            <div className="stat-label">Nodes</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-value">{edgeCount}</div>
            <div className="stat-label">Connections</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item error">
            <div className="stat-value">{errors.length}</div>
            <div className="stat-label">Errors</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item warning">
            <div className="stat-value">{warnings.length}</div>
            <div className="stat-label">Warnings</div>
          </div>
        </div>

        {/* Content */}
        <div className="validation-body">
          {/* Errors Section */}
          {errors.length > 0 && (
            <div className="result-section">
              <div className="section-header error">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M12 8V12M12 16H12.01"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <h3>Errors ({errors.length})</h3>
              </div>

              {Object.entries(errorsByNode).map(
                ([key, data]: [string, any]) => (
                  <div key={key} className="node-error-card">
                    <div className="node-card-header">
                      <div className="node-info">
                        <div className="node-dot error"></div>
                        <span className="node-name">{data.nodeLabel}</span>
                      </div>
                      <span className="error-badge">
                        {data.errors.length}{" "}
                        {data.errors.length === 1 ? "ERROR" : "ERRORS"}
                      </span>
                    </div>
                    <div className="node-card-body">
                      {data.errors.map((error: any, idx: number) => (
                        <div key={idx} className="error-detail">
                          <div className="error-bullet"></div>
                          <div className="error-content">
                            {error.field && (
                              <span className="error-field-name">
                                {error.field}:{" "}
                              </span>
                            )}
                            <span className="error-text">{error.message}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {/* Warnings Section */}
          {warnings.length > 0 && (
            <div className="result-section">
              <div className="section-header warning">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18C1.64537 18.3024 1.55296 18.6453 1.55199 18.9945C1.55101 19.3437 1.64151 19.6871 1.81445 19.9905C1.98738 20.2939 2.23675 20.5467 2.53773 20.7239C2.83871 20.9011 3.18082 20.9962 3.53 21H20.47C20.8192 20.9962 21.1613 20.9011 21.4623 20.7239C21.7633 20.5467 22.0126 20.2939 22.1856 19.9905C22.3585 19.6871 22.449 19.3437 22.448 18.9945C22.447 18.6453 22.3546 18.3024 22.18 18L13.71 3.86C13.5317 3.56611 13.2807 3.32312 12.9812 3.15448C12.6817 2.98585 12.3437 2.89725 12 2.89725C11.6563 2.89725 11.3183 2.98585 11.0188 3.15448C10.7193 3.32312 10.4683 3.56611 10.29 3.86Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <h3>Warnings ({warnings.length})</h3>
              </div>

              <div className="warnings-list">
                {warnings.map((warning, idx) => (
                  <div key={idx} className="warning-card">
                    <div className="warning-icon">⚠️</div>
                    <div className="warning-content">
                      {warning.nodeLabel && (
                        <div className="warning-node">{warning.nodeLabel}</div>
                      )}
                      <div className="warning-text">{warning.message}</div>
                      {warning.nodes && warning.nodes.length > 0 && (
                        <div className="affected-nodes">
                          {warning.nodes.map((node, nodeIdx) => (
                            <span key={nodeIdx} className="node-tag">
                              {node.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Success State */}
          {valid && errors.length === 0 && (
            <div className="success-state-workflow">
              <div className="success-icon-wrapper">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M9 12L11 14L15 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>Workflow is ready!</h3>
              <p>
                All nodes are configured correctly and all connections are
                valid.
              </p>
              {warnings.length > 0 && (
                <p className="success-note">
                  There {warnings.length === 1 ? "is" : "are"} {warnings.length}{" "}
                  warning{warnings.length > 1 ? "s" : ""} that you may want to
                  review.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="validation-footer">
          <button className="footer-btn secondary" onClick={onClose}>
            Close
          </button>
          {!valid && (
            <button className="footer-btn primary" onClick={onClose}>
              Fix Issues
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
